import { apiClient, getApiUrl } from '../lib/apiClient';
import type { PaginationParams } from '../types/api';
import { 
  extractListData, 
  extractSingleData,
  extractPagination,
  type RailsWrappedListResponse,
  type RailsSingleWrappedResponse
} from '../utils/railsResponseHandler';

export interface Report {
  id: string;
  title: string;
  type: 'daily' | 'weekly' | 'monthly' | 'custom';
  date_range: {
    start: string;
    end: string;
  };
  status: 'pending' | 'generating' | 'completed' | 'failed';
  created_at: string;
  completed_at?: string;
  download_url?: string;
  metadata?: Record<string, any>;
}

export interface ReportMetrics {
  student_count: number;
  activity_count: number;
  alert_count: number;
  attendance_rate: number;
  safety_score: number;
  engagement_score: number;
}

export interface ReportStats {
  total_reports: number;
  reports_this_month: number;
  pending_reports: number;
  average_generation_time: number;
}

// Get reports
export const getReports = async (params?: {
  type?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
} & PaginationParams): Promise<{ reports: Report[]; pagination?: any }> => {
  const response = await apiClient.get<RailsWrappedListResponse<Report>>(getApiUrl('/reports'), { params });
  const reports = extractListData<Report>(response.data);
  const pagination = extractPagination(response.data);
  return { reports, pagination };
};

// Get report by ID
export const getReport = async (id: string): Promise<Report> => {
  const response = await apiClient.get<RailsSingleWrappedResponse<Report>>(getApiUrl(`/reports/${id}`));
  return extractSingleData<Report>(response.data);
};

// Generate report
export const generateReport = async (data: {
  type: string;
  date_range: {
    start: string;
    end: string;
  };
  filters?: Record<string, any>;
  format?: 'pdf' | 'excel' | 'csv';
}): Promise<Report> => {
  const response = await apiClient.post<RailsSingleWrappedResponse<Report>>(getApiUrl('/reports/generate'), data);
  return extractSingleData<Report>(response.data);
};

// Get report metrics
export const getReportMetrics = async (params?: {
  date_from?: string;
  date_to?: string;
  school_id?: string;
}): Promise<ReportMetrics> => {
  const response = await apiClient.get<RailsSingleWrappedResponse<ReportMetrics>>(getApiUrl('/reports/metrics'), { params });
  return extractSingleData<ReportMetrics>(response.data);
};

// Get report statistics
export const getReportStats = async (): Promise<ReportStats> => {
  const response = await apiClient.get<RailsSingleWrappedResponse<ReportStats>>(getApiUrl('/reports/stats'));
  return extractSingleData<ReportStats>(response.data);
};

// Export report
export const exportReport = async (
  reportId: string,
  options: {
    format: 'pdf' | 'excel' | 'csv';
  }
): Promise<Blob> => {
  const response = await apiClient.get(
    getApiUrl(`/reports/${reportId}/export`),
    {
      params: options,
      responseType: 'blob'
    }
  );
  return response.data;
};

// Download report
export const downloadReport = async (id: string): Promise<Blob> => {
  const response = await apiClient.get(getApiUrl(`/reports/${id}/download`), {
    responseType: 'blob'
  });
  return response.data;
};

// Delete report
export const deleteReport = async (id: string): Promise<void> => {
  await apiClient.delete(getApiUrl(`/reports/${id}`));
};

// Schedule report
export const scheduleReport = async (data: {
  type: string;
  schedule: 'daily' | 'weekly' | 'monthly';
  time: string;
  recipients: string[];
  filters?: Record<string, any>;
}): Promise<any> => {
  const response = await apiClient.post<RailsSingleWrappedResponse<any>>(getApiUrl('/reports/schedule'), data);
  return extractSingleData<any>(response.data);
};